import React, { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import './OrbitDemoPage.css';

/**
 * Orbit Demo Page - Full-Screen Video Background with Scrollytelling
 * 
 * Features a YouTube video as the full-window background with scrolling content overlaid
 */

const contentSections = [
  {
    isHero: true,
    title: "Orbit — a private, adaptive explainer portal for your clinical trial",
    headline: "A private patient portal that adapts to explain your clinical trial",
    subhead: "Powered by an NIH-supported user-adaptive visual narrative engine used by top research institutions worldwide. Orbit is a private, personalized platform that continuously translates a study's science into clear, visual explanations that automatically adapt to each participant or caregiver's language, understanding, and engagement.",
    clarifier: "Each study has its own private, secure portal, accessible only to enrolled participants and caregivers.",
    ctaText: "Trial investigators: apply to Orbit's pilot program",
    ctaSubtext: "(No obligation or cost)"
  },
  {
    title: "Why this exists",
    description: "In many trials, participants and caregivers rely on YouTube and social media to make sense of complex research — often encountering incomplete or inaccurate information.\n\nOrbit provides a scientifically grounded alternative: a private portal linked directly to the study's own materials and research output, designed to evolve alongside the trial.",
  },
  {
    title: "What participants and caregivers experience",
    bulletPoints: [
      "A secure, study-specific scrolling feed, designed for how people actually consume information",
      "Short, narrated visual explainers that adapt to language, comprehension level, and interaction",
      "Ongoing access to new research from the study team, translated into easy-to-digest visual stories",
      "A sense of continuity and contribution as the science progresses"
    ],
  },
  {
    title: "How the portal adapts",
    intro: "Orbit is not a static content library.",
    subtitle: "The portal:",
    bulletPoints: [
      "Adjusts explanations based on user language and comprehension level",
      "Adapts presentation based on how users interact (e.g., pacing, revisiting topics)",
      "Surfaces study concepts and research updates in forms that are easier to understand over time"
    ],
    footer: "All adaptation is based on investigator-approved content."
  },
  {
    title: "What this is — and what it isn't",
    sections: [
      {
        label: "This is:",
        text: "A private, adaptive communication layer that connects participants and caregivers directly to a study's science."
      },
      {
        label: "This is not:",
        bulletPoints: [
          "An animation studio or media service",
          "A recruitment, reminder, or compliance platform",
          "A replacement for EDC, ePRO, DHTs, or scheduling systems"
        ]
      }
    ]
  }

];

const OrbitDemoPage = () => {
  const [isVideoMode, setIsVideoMode] = useState(false);
  const iframeRef = React.useRef(null);

  // Hide WelcomeChatbot on this page
  useEffect(() => {
    // Find the chatbot container - it has class 'chatbot-container'
    const chatbotElements = document.querySelectorAll('.chatbot-container, .chatbot-fab, [class*="chatbot"]');
    chatbotElements.forEach(el => {
      el.style.display = 'none';
    });
    
    return () => {
      // Restore on unmount
      chatbotElements.forEach(el => {
        el.style.display = '';
      });
    };
  }, []);

  const handleWatchDemo = () => {
    setIsVideoMode(true);
    // Switch to the demo video with audio, controls, and looping
    if (iframeRef.current) {
      const iframe = iframeRef.current;
      iframe.src = "https://www.youtube.com/embed/_etTtJ0rTaU?autoplay=1&mute=0&loop=1&playlist=_etTtJ0rTaU&controls=1&playsinline=1&rel=0&modestbranding=1";
    }
  };

  const handleReadMore = () => {
    setIsVideoMode(false);
    // Restore background video (muted, looping)
    if (iframeRef.current) {
      const iframe = iframeRef.current;
      iframe.src = "https://www.youtube.com/embed/HKIvoSNObBo?autoplay=1&mute=1&loop=1&playlist=HKIvoSNObBo&controls=0&playsinline=1&rel=0&modestbranding=1";
    }
  };

  return (
    <div className={`orbit-fullscreen-container ${isVideoMode ? 'video-mode' : ''}`}>
      {/* Full-Screen Background Video */}
      <div className="orbit-video-background">
        <iframe
          ref={iframeRef}
          src="https://www.youtube.com/embed/HKIvoSNObBo?autoplay=1&mute=1&loop=1&playlist=HKIvoSNObBo&controls=0&playsinline=1&rel=0&modestbranding=1"
          title="Orbit Background Video"
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          className="orbit-background-iframe"
        />
        <div className={`orbit-video-overlay ${isVideoMode ? 'orbit-video-overlay-hidden' : ''}`} />
      </div>

      {/* Floating Toggle Button */}
      <Button
        className="orbit-toggle-button"
        onClick={() => isVideoMode ? handleReadMore() : handleWatchDemo()}
      >
        {isVideoMode ? 'Read More' : 'Watch Demo'}
      </Button>

      {/* Scrolling Content Over Video */}
      <div className={`orbit-scroll-content ${isVideoMode ? 'orbit-scroll-content-hidden' : ''}`}>
        {contentSections.map((section, index) => (
          <div key={index} className="orbit-content-section">
            {/* Hero Section */}
            {section.isHero && (
              <>
                <div className="orbit-logo-header">
                  <span className="orbit-logo-text">Orbit</span>
                  <span className="orbit-logo-byline">by Snorkle Labs</span>
                </div>
                <h2 className="orbit-headline">{section.headline}</h2>
                <p className="orbit-subhead">{section.subhead}</p>
                <p className="orbit-clarifier">{section.clarifier}</p>
                <div className="orbit-cta-container">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={() => window.location.href = '/orbit-pilot'}
                    className="orbit-cta-button"
                  >
                    {section.ctaText}
                  </Button>
                  <p className="orbit-cta-subtext">{section.ctaSubtext}</p>
                </div>
              </>
            )}

            {/* Standard Section */}
            {!section.isHero && (
              <>
                <h2 className="orbit-section-title">{section.title}</h2>
                
                {section.intro && <p className="orbit-intro">{section.intro}</p>}
                
                {section.subtitle && <p className="orbit-section-subtitle">{section.subtitle}</p>}
                
                {section.description && (
                  <p className="orbit-description">{section.description}</p>
                )}
                
                {section.bulletPoints && (
                  <ul className="orbit-bullet-list">
                    {section.bulletPoints.map((point, i) => (
                      <li key={i}>{point}</li>
                    ))}
                  </ul>
                )}
                
                {section.sections && section.sections.map((subsection, i) => (
                  <div key={i} className="orbit-subsection">
                    <p className="orbit-subsection-label">{subsection.label}</p>
                    {subsection.text && <p className="orbit-subsection-text">{subsection.text}</p>}
                    {subsection.bulletPoints && (
                      <ul className="orbit-bullet-list">
                        {subsection.bulletPoints.map((point, j) => (
                          <li key={j}>{point}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
                
                {section.footer && <p className="orbit-footer-note">{section.footer}</p>}
              </>
            )}
          </div>
        ))}

        {/* Final CTA Section */}
        <div className="orbit-content-section orbit-final-cta">
          <h2 className="orbit-gradient-text">Interested in exploring whether an adaptive explainer portal could support your current study?</h2>
          <Button
            variant="light"
            size="lg"
            onClick={() => window.location.href = '/orbit-pilot'}
            className="orbit-cta-button"
          >
            View pilot details / request early access
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OrbitDemoPage;
